<template>
    <form @submit.prevent="onSubmit()">
        <div
            class="w-full"
            :class="showNameFields
                ? ''
                : 'pb-8 lg:px-0'"
        >
            <div 
                :class="{ 
                    'flex justify-center gap-8': !showNameFields, 
                    'w-full flex justify-evenly p-4 mb-2 lg:justify-start lg:px-0 lg:gap-4': showNameFields 
                }">
                <label for="newsletter-footer-male" class="label">
                    <input
                        id="newsletter-footer-male"
                        v-model="gender"
                        type="radio"
                        name="newsletterGender"
                        value="1"
                        class="radio mr-2"
                        :class="{ 'border-sale': genderError }"
                        @change="clearGenderError"
                    >
                    <span class="label-text" :class="{ 'font-normal text-base-color': !showNameFields }">{{ showNameFields ? $t('newsletter.maleLabel') : $t('layout.footer.newsletter.maleLabel') }}</span>
                </label>
                <label for="newsletter-footer-female" class="label">
                    <input
                        id="newsletter-footer-female"
                        v-model="gender"
                        type="radio"
                        name="newsletterGender"
                        value="2"
                        class="radio mr-2"
                        :class="{ 'border-sale': genderError }"
                        @change="clearGenderError"
                    >
                    <span class="label-text" :class="{ 'font-normal text-base-color': !showNameFields }">{{ showNameFields ? $t('newsletter.femaleLabel') : $t('layout.footer.newsletter.femaleLabel') }}</span>
                </label>
            </div>
            <div v-if="genderError" class="flex justify-center gap-1 mt-1">
                <span class="text-xs text-sale">{{ $t('layout.footer.newsletter.validation.required') }}</span>
            </div>
        </div>
        <div v-if="showNameFields" class="w-full flex gap-3 pb-2">
            <div class="w-1/2 form-control">
                <label for="newsletter-firstName" class="sr-only">{{ $t('newsletter.firstNameLabel') }}</label>
                <input
                    id="newsletter-firstName"
                    v-model="firstName"
                    type="text"
                    class="input w-full"
                    :placeholder="$t('newsletter.firstNamePlaceholder')"
                >
            </div>
            <div class="w-1/2 form-control">
                <label for="newsletter-lastName" class="sr-only">{{ $t('newsletter.lastNameLabel') }}</label>
                <input
                    id="newsletter-lastName"
                    v-model="lastName"
                    type="text"
                    class="input w-full"
                    :placeholder="$t('newsletter.lastNamePlaceholder')"
                >
            </div>
        </div>
        <div class="w-full">
            <div
                class="flex flex-col gap-2"
                :class="{ 'sm:flex-row': !displaySmall && !showNameFields }"
            >
                <div
                    class="flex flex-col"
                    :class="{
                        'sm:w-3/5 md:w-4/6 xl:w-3/5 2xl:w-4/6': !displaySmall && !showNameFields,
                        'w-full': displaySmall || showNameFields
                    }"
                >
                    <label for="newsletter-footer-email" class="sr-only">{{ showNameFields ? $t('newsletter.emailLabel') : $t('layout.footer.newsletter.emailLabel') }}</label>
                    <input
                        id="newsletter-footer-email"
                        v-model="email"
                        type="email"
                        :class="[
                            'input',
                            showNameFields ? 'w-full' : 'placeholder:text-base-content text-sm',
                            { 'border-sale': emailError }
                        ]"
                        :placeholder="showNameFields ? $t('newsletter.emailPlaceholder') : $t('layout.footer.newsletter.emailPlaceholder')"
                        @input="clearEmailError"
                        @blur="validateEmail"
                    >
                    <div v-if="emailError" class="flex justify-start gap-1 mt-1">
                        <span class="text-xs text-sale">{{ emailErrorMessage }}</span>
                    </div>
                </div>
                <button
                    type="submit"
                    :disabled="loading"
                    :class="[
                        'btn btn-primary',
                        showNameFields ? 'w-full' : 'text-xs',
                        {
                            'sm:w-2/5 md:w-2/6 xl:w-2/5 2xl:w-2/6': !displaySmall && !showNameFields,
                            'w-full': displaySmall || showNameFields
                        }
                    ]"
                    @click.prevent="onSubmit()"
                >
                    {{ showNameFields ? $t('newsletter.submitButton') : $t('layout.footer.newsletter.submitButton') }}
                    <span v-if="loading" class="loading" />
                </button>
            </div>
        </div>
        <div v-if="showLegal" class="my-4 text-xs text-base-content text-center lg:text-left">
            <i18n-t
                keypath="layout.footer.newsletter.rightOfReturnText"
                tag="span"
                scope="global"
            >
                <MiscLink class="link" :to="$t('layout.footer.newsletter.dataProtectionPath')" aria-label="Data protection declaration">
                    {{ $t('layout.footer.newsletter.dataProtectionLink') }}
                </MiscLink>
            </i18n-t>
        </div>
    </form>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'

const props = withDefaults(defineProps<{
    displaySmall?: boolean,
    popup?: Schemas["Popup"] | null,
    showNameFields?: boolean,
    showLegal?: boolean,
}>(), {
    displaySmall: false,
    popup: null,
    showNameFields: false,
    showLegal: true,
})

const emit = defineEmits(['onSuccess', 'onError', 'afterSubmit'])
const { t } = useI18n()
const { showNotification } = useNotification()
const nuxtApp = useNuxtApp()
const { subscribeToNewsletter, salutations } = useNewsletterSubscription()
const loading = ref(false)
const gender = ref(props.showNameFields ? '1': '')
const email = ref('')
const firstName = ref('')
const lastName = ref('')

// Validation states
const genderError = ref(false)
const emailError = ref(false)
const emailErrorMessage = ref('')

// Validation functions
function clearGenderError() {
    genderError.value = false
}

function clearEmailError() {
    emailError.value = false
    emailErrorMessage.value = ''
}

function validateEmail() {
    if (!email.value.trim()) {
        emailError.value = true
        emailErrorMessage.value = t('layout.footer.newsletter.validation.required')
        return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email.value)) {
        emailError.value = true
        emailErrorMessage.value = t('layout.footer.newsletter.validation.emailInvalid')
        return false
    }

    emailError.value = false
    emailErrorMessage.value = ''
    return true
}

function validateGender() {
    if (!gender.value) {
        genderError.value = true
        return false
    }

    genderError.value = false
    return true
}

function validateForm() {
    const isEmailValid = validateEmail()
    const isGenderValid = validateGender()

    return isEmailValid && isGenderValid
}

function resetForm () {
    gender.value = ''
    email.value = ''
    firstName.value = ''
    lastName.value = ''
    clearGenderError()
    clearEmailError()
}

async function onSubmit () {
    // Track newsletter button click
    if (props.popup) {
        nuxtApp.callHook('popupClicked', {
            popup: props.popup,
            pageType: 'newsletter_subscribe',
            click_text: t('layout.footer.newsletter.submitButton')
        })
    }

    const isValid = validateForm()
    if (!isValid) {
        return
    }

    loading.value = true

    try {
        const salutationId = gender.value === '2' ? salutations.female : salutations.male
        const firstNameValue = props.showNameFields && firstName.value.trim() !== '' ? firstName.value : undefined
        const lastNameValue = props.showNameFields && lastName.value.trim() !== '' ? lastName.value : undefined

        await subscribeToNewsletter(
            email.value,
            salutationId,
            firstNameValue,
            lastNameValue
        )

        resetForm()

        // Only show notification if NOT displaySmall
        if (!props.displaySmall) {
            const successKey = props.showNameFields ? 'newsletter.subscribeSuccess' : 'layout.footer.newsletter.subscribeSuccess'
            showNotification(t(successKey), 'success')
        }

        loading.value = false
        emit('onSuccess')
    } catch (e) {
        loading.value = false
        showNotification(t('layout.footer.newsletter.subscribeError'), 'error')
        console.error(e)
        emit('onError')
    }

    emit('afterSubmit')
}
</script>
